/*
 * File: OrderPage.js
 * Project: mp-mobile-app
 *
 * Created by Rob Rothe
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Shows an order details page
 */
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import EditIcon from "@material-ui/icons/Edit";
import UIProgress from "components/ui/UIProgress";
import UINotFound from "components/ui/UINotFound";
import UIFab from "components/ui/UIFab";
import OrderDetails from "../components/OrderDetails";
import OrderForm from "../components/OrderForm";
import UIFormDialog from "components/ui/UIFormDialog";
import OrdersAppStore from "services/orders/store/models/OrdersAppStore";
import { clone } from "mobx-state-tree";
import { useStore } from "utils/useStore";

const OrderPage = ({ appStore, match }) => {
  const [loading, setLoading] = useState(true);
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [order, setOrder] = useState(null);

  const orderId = match.params.orderId;
  const store = useStore(OrdersAppStore);

  const fetchOrder = useCallback(async () => {
    setLoading(true);
    const fetched = await store.fetch(orderId);
    setOrder(fetched);
    setLoading(false);
  }, [orderId, store]);

  useEffect(() => {
    appStore.setTitle("Orders");
    appStore.setRightNav();
    fetchOrder(orderId);
  }, [appStore, fetchOrder, orderId]);

  const parentCallBack = () => {
    toggleFormDialog();
    fetchOrder();
  };

  const toggleFormDialog = () => {
    setShowFormDialog(!showFormDialog);
  };

  const orderForm = () => {
    if (!order) return;
    const editOrder = clone(order);
    return (
      <OrderForm order={editOrder} parentCallBack={parentCallBack} editing />
    );
  };

  if (loading) return <UIProgress />;
  if (!order)
    return <UINotFound message={`No order found with ID ${orderId}`} />;
  return (
    <React.Fragment>
      <UIFormDialog
        open={showFormDialog}
        toggleDialog={toggleFormDialog}
        title={"Edit Order"}
        viewPage={orderForm}
      />
      <OrderDetails order={order} />
      <UIFab
        icon={<EditIcon id="icon-edit-order" />}
        name={"Edit Order"}
        handleClick={toggleFormDialog}
      />
    </React.Fragment>
  );
};

OrderPage.propTypes = {
  appStore: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired
};

export default OrderPage;
